<template>
    <div class="scope-multiselect-container" :style="styles">
        <div v-if="label" class="scope-multiselect-label">
            {{ normalizedLabel }}
            <span
                v-if="showRequiredMark"
                class="scope-multiselect-label__required"
            >
                *
            </span>
        </div>
        <VueMultiselect
            ref="multiselect"
            :class="[
                'scope-multiselect',
                `-${size}`,
                { '-with-search': searchable, '-invalid': invalid }
            ]"
            v-model="model"
            :multiple="multiple"
            :options="optionsDropdown"
            :label="useObjectOptions ? 'name' : undefined"
            :searchable="searchable"
            :disabled="disabled"
            close-on-select
            :show-labels="false"
            :placeholder="placeholderProp"
            :allowEmpty="allowEmpty"
        >
            <template #singleLabel="{ option }">
                <div class="multiselect__option-content">
                    <Icon
                        v-if="option?.icon"
                        :icon="option.icon"
                        class="multiselect__option-icon"
                        :key="option.icon"
                    />
                    <span>{{ option?.name ?? option }}</span>
                </div>
            </template>
            <template #option="{ option }">
                <div class="multiselect__option-content">
                    <Icon
                        v-if="option?.icon"
                        :icon="option.icon"
                        class="multiselect__option-icon"
                        :key="option.icon"
                    />
                    <span>{{ option?.name ?? option }}</span>
                </div>
            </template>
            <template #selection="{ values }">
                <span class="multiselect__single" v-if="values.length">
                    {{ values.length }} options selected
                </span>
            </template>
        </VueMultiselect>
        <div
            v-if="invalid && errorMessage"
            class="scope-multiselect-container__error"
        >
            {{ errorMessage }}
        </div>
        <div
            :class="[
                'scope-multiselect-container__icon-wrapper',
                `-${size}`,
                { '-with-search': searchable, '-label': label }
            ]"
        >
            <Icon
                v-if="searchable"
                icon="Search"
                :class="[
                    'search-item',
                    isNotEmpty ? '-is-not-empty' : '-is-empty'
                ]"
            />
            <Icon
                icon="ChevronDown"
                :class="[
                    'select-item',
                    isNotEmpty ? '-is-not-empty' : '-is-empty',
                    { '-opened': isOpened }
                ]"
            />
        </div>
    </div>
</template>

<script setup>
import VueMultiselect from 'vue-multiselect';
import { ref, computed } from 'vue';
import Icon from './Icon.vue';

const props = defineProps({
    size: {
        type: String,
        default: 'md'
    },
    searchable: Boolean,
    options: Array,
    placeholder: String,
    label: String,
    invalid: Boolean,
    errorMessage: String,
    disabled: Boolean,
    multiple: Boolean,
    allowEmpty: {
        type: Boolean,
        default: true
    },
    required: { type: Boolean, default: false },
    width: {
        type: String,
        default: '250px'
    }
});

const styles = computed(() => (props.width ? { width: props.width } : null));
const showRequiredMark = computed(() =>
    Boolean(props.required || /\*\s*$/.test(props.label || ''))
);
const normalizedLabel = computed(() =>
    (props.label || '').replace(/\s*\*$/, '')
);

const multiselect = ref(null);
const isOpened = computed(() => multiselect.value && multiselect.value.isOpen);
const isNotEmpty = computed(() => Boolean(model.value && model.value.length));

const isOptionsObject = computed(() =>
    props.options.every((item) => typeof item === 'object' && item !== null)
);

const optionsDropdown = computed(() => props.options);

const useObjectOptions = computed(() => isOptionsObject.value);

const placeholderProp = computed(() => {
    if (props.searchable) {
        if (!model.value) return props.placeholder;
        return typeof model.value === 'object'
            ? model.value?.name
            : model.value;
    } else {
        return props.placeholder;
    }
});

const model = defineModel({
    get(value) {
        if (value == null) return value;
        if (!useObjectOptions.value) return value;
        if (typeof value === 'object') return value;
        return props.options.find((option) => option.name === value) ?? value;
    },
    set(value) {
        if (value == null) return value;
        if (!useObjectOptions.value) return value;
        if (typeof value === 'object') return value;
        return props.options.find((option) => option.name === value) ?? value;
    }
});
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.scope-multiselect-container {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:has(.multiselect--disabled) {
        .scope-multiselect-container__icon-wrapper {
            display: none;
        }
    }

    .scope-multiselect-label {
        color: $text-high-emphasis;
        @include fz-14;
        font-weight: 500;

        &__required {
            color: $primary-accent;
        }
    }

    ::v-deep(.scope-multiselect) {
        color: $text-high-emphasis;
        min-height: unset;

        .multiselect__tags {
            display: flex;
            align-items: center;

            border-radius: 10px;
            min-height: unset;
            height: 100%;
            padding: 8px 36px 8px 12px;

            border: 1px solid $border;
            background: $card-background;
            @include fz-14;
            font-weight: 500;

            cursor: pointer;
        }

        &:not(.multiselect--disabled):focus,
        &:not(.multiselect--disabled):hover {
            .multiselect__tags {
                border-color: $primary-accent;
            }
        }

        &.-lg {
            height: 40px;
        }

        &.-md {
            height: 36px;
        }

        &.-sm {
            height: 32px;

            .multiselect__tags {
                border-radius: 8px;
            }
        }

        &.multiselect--disabled,
        &.multiselect--disabled .multiselect__select {
            cursor: not-allowed;
            background: none;
        }

            .multiselect {
                &__input,
                &__single {
                    min-height: unset;
                    line-height: unset;
                    padding: 0;
                    width: unset;
                    @include transition-default(border);
                    box-sizing: border-box;
                    margin-bottom: 0;
                    background: $card-background;
                    white-space: nowrap;
                    overflow: hidden;

                    @include fz-14;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                &__input {
                    border: none;
                    outline: none;
                    box-shadow: none;
                }

            &__placeholder {
                color: $text-mid-emphasis;
                display: inline-block;
                margin-bottom: unset;
                padding-top: unset;
            }

            &__content-wrapper {
                position: absolute;
                display: block;
                background: $card-background;
                width: 100%;
                max-height: 240px;
                overflow: auto;
                z-index: 9999;
                -webkit-overflow-scrolling: touch;

                padding: 6px 4px;
                margin-top: 4px;
                border-radius: 10px;
                border: 1px solid $border;

                scrollbar-color: $text-mid-emphasis transparent;
                scrollbar-width: thin;

                &::-webkit-scrollbar {
                    width: 6px;
                }

                &::-webkit-scrollbar-track {
                    background: transparent;
                }

                &::-webkit-scrollbar-thumb {
                    background-color: $text-mid-emphasis;
                    border-radius: 6px;
                }
            }

            &__select {
                line-height: 16px;
                display: block;
                position: absolute;
                box-sizing: border-box;
                width: 100%;
                height: 100%;
                right: 1px;
                top: 1px;
                padding: 4px 8px;
                margin: 0;
                text-decoration: none;
                text-align: center;
                cursor: pointer;
                @include transition-default(transform);
                z-index: 10;

                &.multiselect--active {
                    transform: rotateZ(180deg);
                }

                &::before {
                    content: unset;
                    display: inline-block;
                    width: 20px;
                    height: 20px;
                    margin-top: 0px;
                    color: unset;
                    border: unset;
                }
            }

            &__option {
                font-family: 'Geist', sans-serif;
                font-optical-sizing: auto;
                @include fz-14;
                font-weight: 500;
                color: $text-high-emphasis;
                padding: 8px;
                border-radius: 8px;
                color: $text-high-emphasis;
                line-height: unset;
                min-height: unset;
                max-height: 36px;
                @include transition-default(background);

                &--selected,
                &--highlight.multiselect__option--selected {
                    background: $card-neutral-background !important;
                    border-radius: 8px;
                    color: unset;
                }

                &--highlight {
                    background: $card-neutral-background !important;
                    color: $text-high-emphasis;
                    border-radius: 8px;
                }

                &--selected {
                    font-weight: 500;
                }
            }

            &__content {
                display: flex !important;
                flex-direction: column;
                gap: 4px;

                list-style: none;
                padding: 0;
                margin: 0;
                min-width: 100%;
                vertical-align: top;

                text-decoration: none;
                text-transform: none;
                vertical-align: middle;
                position: relative;
                cursor: pointer;
                white-space: nowrap;
            }

            &__element span {
                display: block;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }
    }

    ::v-deep(.multiselect__option-content) {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;

        span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    ::v-deep(.multiselect__option-icon) {
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
    }

    ::v-deep(.scope-multiselect.-with-search) {
        .multiselect__tags {
            padding: 8px 36px 8px 36px;
        }
    }

    &__icon-wrapper {
        width: 100%;
        height: 36px;

        position: absolute;
        z-index: 50;
        top: 0;

        display: flex;
        align-items: center;
        justify-content: flex-end;
        pointer-events: none;

        padding: 0 12px;

        &.-lg {
            height: 40px;
        }

        &.-sm {
            height: 32px;
        }

        &.-label {
            top: 28.8px;
        }

        .select-item,
        .search-item {
            &.-is-not-empty {
                color: $text-high-emphasis;
            }

            &.-is-empty {
                color: $text-mid-emphasis;
            }
        }

        .select-item {
            transition-duration: 0.4s;

            &.-opened {
                transform: rotate(180deg);
            }
        }

        &.-with-search {
            justify-content: space-between;
        }
    }

    ::v-deep(.scope-multiselect.-invalid) {
        .multiselect__tags {
            border-color: $border-error;
        }
    }

    &__error {
        @include fz-12;
        font-weight: 500;
        color: $border-error;
    }
}

body[theme='dark'] .scope-multiselect-container {
    .scope-multiselect-label {
        color: $text-high-emphasis;
    }

    ::v-deep(.scope-multiselect) {
        color: $text-high-emphasis;

        .multiselect__tags {
            background: $background-secondary;
            border: 1px solid $border-secondary;
        }

        &:not(.multiselect--disabled):focus,
        &:not(.multiselect--disabled):hover {
            .multiselect__tags {
                border-color: $primary-accent;
            }
        }

        &.multiselect--disabled,
        &.multiselect--disabled .multiselect__select {
            background: none;
        }

        .multiselect {
            &__input,
            &__single {
                background: $background-secondary;
            }

            &__placeholder {
                color: $text-mid-emphasis;
            }

            &__content-wrapper {
                background: $card-neutral-background;
                border: 1px solid $border-secondary;
            }

            &__option {
                color: $text-high-emphasis;

                &--selected,
                &--highlight.multiselect__option--selected {
                    background: $card-background;
                    color: unset;
                }

                &--highlight {
                    background: $background-secondary !important;
                    color: $text-high-emphasis;
                }

                .custom-option {
                    background: $card-neutral-background;

                    .custom-check-icon {
                        color: $primary-accent;
                    }

                    &:hover {
                        background: $background-secondary;
                    }

                    &.multiselect__option--selected {
                        background: $card-background !important;
                    }
                }
            }
        }
    }

    &__icon-wrapper {
        .select-item,
        .search-item {
            &.-is-not-empty {
                color: $text-high-emphasis;
            }

            &.-is-empty {
                color: $text-mid-emphasis;
            }
        }
    }

    ::v-deep(.scope-multiselect.-invalid) {
        .multiselect__tags {
            border-color: $border-error;
        }
    }
}
</style>
